/**
 * CameraCaptureModal — webcam selfie capture for staff profile photos.
 *
 * Flow:
 *   1. Opens with `open=true` → requests camera via `navigator.mediaDevices.getUserMedia`.
 *   2. Streams to a <video> element with a round overlay so users frame their face.
 *   3. "Snap" copies the current frame into a hidden <canvas>, exports a data URL,
 *      and hands it to `onCapture(dataUrl)` which the parent feeds into the
 *      existing <AvatarCropModal /> for fine-tune crop + zoom.
 *   4. Always stops the MediaStream when the modal closes (privacy / battery).
 *
 * Errors:
 *   - Permission denied → friendly message + "Pick a file instead" hint.
 *   - No camera attached → same fallback.
 *   - HTTPS required → browsers gate getUserMedia behind secure context; we
 *     surface that explicitly when the API is missing.
 */
import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Camera, RefreshCw, X, AlertTriangle } from "lucide-react";

const TARGET_W = 720;
const TARGET_H = 720;

export default function CameraCaptureModal({ open, onClose, onCapture }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [error, setError] = useState("");
  const [ready, setReady] = useState(false);
  // Countdown state: null (idle), 3, 2, 1, then 0 triggers actual capture + flash.
  const [countdown, setCountdown] = useState(null);
  const [flash, setFlash] = useState(false);
  const countdownTimerRef = useRef(null);
  const flashTimerRef = useRef(null);

  // Prefer the user-facing camera; fall back to whatever's available.
  const startStream = async () => {
    setError("");
    setReady(false);
    if (typeof navigator === "undefined" ||
        !navigator.mediaDevices?.getUserMedia) {
      setError(
        "Your browser doesn't expose a camera here. Camera capture needs an HTTPS connection — try uploading a file instead."
      );
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
        setReady(true);
      }
    } catch (err) {
      const name = err?.name || "";
      if (name === "NotAllowedError" || name === "PermissionDeniedError") {
        setError("Camera permission was blocked. Allow camera access in your browser settings, then try again.");
      } else if (name === "NotFoundError" || name === "OverconstrainedError") {
        setError("No camera found on this device. You can upload a photo file instead.");
      } else {
        setError("Couldn't start the camera. Please try again, or upload a photo file instead.");
      }
    }
  };

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
    setReady(false);
  };

  // Start the camera when the modal opens; clean up on close.
  useEffect(() => {
    if (open) startStream();
    else stopStream();
    return stopStream;
  }, [open]);

  const handleSnap = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !ready) return;
    // Square crop centred on the larger dimension so we feed a 1:1 image
    // into the crop modal — fewer surprises for the user.
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    if (!vw || !vh) return;
    const side = Math.min(vw, vh);
    const sx = (vw - side) / 2;
    const sy = (vh - side) / 2;
    canvas.width = TARGET_W;
    canvas.height = TARGET_H;
    const ctx = canvas.getContext("2d");
    // Mirror the snap so the selfie matches the live preview (which is also mirrored below).
    ctx.translate(TARGET_W, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, sx, sy, side, side, 0, 0, TARGET_W, TARGET_H);
    const dataUrl = canvas.toDataURL("image/png", 0.95);
    // Quick "camera flash" effect, then hand off to the crop modal.
    setFlash(true);
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    flashTimerRef.current = setTimeout(() => {
      setFlash(false);
      stopStream();
      onCapture(dataUrl);
    }, 240);
  };

  // Start a 3-2-1 countdown, then fire the snap. Cancelled when the modal
  // closes or the user hits Restart.
  const tickCountdown = (n) => {
    setCountdown(n);
    if (n <= 1) {
      countdownTimerRef.current = setTimeout(() => {
        setCountdown(null);
        handleSnap();
      }, 800);
    } else {
      countdownTimerRef.current = setTimeout(() => tickCountdown(n - 1), 800);
    }
  };

  const handleSnapClick = () => {
    if (!ready || countdown !== null) return;
    tickCountdown(3);
  };

  const cancelCountdown = () => {
    if (countdownTimerRef.current) {
      clearTimeout(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    setCountdown(null);
  };

  // Make sure no timers leak between mounts / restarts.
  useEffect(() => {
    if (!open) {
      cancelCountdown();
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
      setFlash(false);
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        className="max-w-md max-h-[90vh] overflow-y-auto"
        data-testid="camera-capture-modal"
      >
        <DialogTitle className="text-lg font-semibold inline-flex items-center gap-2">
          <Camera className="w-4 h-4 text-purple-500" /> Take a selfie
        </DialogTitle>
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Frame your face inside the circle, then hit Snap. You&apos;ll get to fine-tune the crop next.</p>

        {error ? (
          <div className="mt-5 rounded-xl border border-amber-200 dark:border-amber-900/40 bg-amber-50 dark:bg-amber-900/10 p-4 text-sm text-amber-800 dark:text-amber-200 flex items-start gap-2" data-testid="camera-error">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <div>{error}</div>
          </div>
        ) : (
          <div className="relative w-full mt-4 bg-slate-900 rounded-xl overflow-hidden" style={{ aspectRatio: "1 / 1" }}>
            <video
              ref={videoRef}
              playsInline
              muted
              autoPlay
              className="absolute inset-0 w-full h-full object-cover"
              style={{ transform: "scaleX(-1)" }}
              data-testid="camera-preview"
            />
            {/* Round face-frame overlay */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "radial-gradient(circle at center, transparent 47%, rgba(0,0,0,0.55) 48%)",
              }}
            />
            {!ready && !error && (
              <div className="absolute inset-0 flex items-center justify-center text-slate-300 text-xs">
                Starting camera…
              </div>
            )}
            {/* 3-2-1 countdown overlay */}
            {countdown !== null && (
              <div
                key={countdown}
                className="absolute inset-0 flex items-center justify-center pointer-events-none"
                data-testid="camera-countdown"
              >
                <div className="text-white font-bold drop-shadow-lg animate-countdown-pop"
                  style={{ fontSize: "8rem", lineHeight: 1, textShadow: "0 4px 24px rgba(0,0,0,0.6)" }}>
                  {countdown}
                </div>
              </div>
            )}
            {/* Camera-flash white-out at capture moment */}
            {flash && (
              <div
                className="absolute inset-0 bg-white pointer-events-none transition-opacity duration-200"
                style={{ opacity: 0.85 }}
                data-testid="camera-flash"
              />
            )}
          </div>
        )}

        <canvas ref={canvasRef} className="hidden" />

        <div className="flex items-center justify-between gap-2 mt-5">
          <Button
            type="button"
            variant="outline"
            onClick={() => { cancelCountdown(); startStream(); }}
            disabled={ready && !error && countdown === null}
            data-testid="camera-restart-btn"
          >
            <RefreshCw className="w-4 h-4 mr-1" /> Restart
          </Button>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              data-testid="camera-close-btn"
            >
              <X className="w-4 h-4 mr-1" /> Close
            </Button>
            {countdown !== null ? (
              <Button
                type="button"
                onClick={cancelCountdown}
                className="bg-rose-600 hover:bg-rose-700 text-white"
                data-testid="camera-cancel-countdown-btn"
              >
                Cancel ({countdown})
              </Button>
            ) : (
              <Button
                type="button"
                onClick={handleSnapClick}
                disabled={!ready || !!error}
                className="bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="camera-snap-btn"
              >
                <Camera className="w-4 h-4 mr-1" /> Snap in 3…
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
